'use client'

import { useAtomValue } from 'jotai'
import { type ReactNode, useLayoutEffect, useRef } from 'react'

import { cn } from '@/lib/utils'
import { orderFormPromptFillAtomFamily } from '@/state/order'

// A row mounting this long after the prompt filled the form (a new leg rendering a beat later)
// still flashes; anything later is an unrelated remount.
const FRESH_MS = 1500
// Legs light up one after another rather than all at once.
const STAGGER_MS = 60

type FillArea = { kind: 'leg'; symbol: string } | { kind: 'price' } | { kind: 'tab' }

/**
 * Wraps a part of the order form and flashes it once (`.prompt-fill-flash` in globals.css: a
 * cyan tint and hairline that fade out) when the order-form prompt has just filled that part
 * in. The class is restarted in place — never via a remounting `key` — so the inputs inside
 * keep their state.
 */
export function PromptFillFlash({
  cardId,
  area,
  className,
  children,
}: {
  cardId: string
  area: FillArea
  className?: string
  children: ReactNode
}) {
  const fill = useAtomValue(orderFormPromptFillAtomFamily(cardId))
  const hit =
    fill.nonce > 0 && (area.kind === 'leg' ? fill.legs.includes(area.symbol) : fill[area.kind])
  const ref = useRef<HTMLDivElement>(null)
  // Top to bottom, like the form reads: the tab, then each leg in order, then the price.
  const order =
    area.kind === 'tab'
      ? 0
      : area.kind === 'leg'
        ? 1 + Math.max(0, fill.legs.indexOf(area.symbol))
        : 1 + fill.legs.length

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !hit || Date.now() - fill.at > FRESH_MS) return
    el.classList.remove('prompt-fill-flash')
    // Force a reflow so re-adding the class restarts the animation on a repeat fill.
    void el.offsetWidth
    el.style.animationDelay = `${order * STAGGER_MS}ms`
    el.classList.add('prompt-fill-flash')
  }, [fill.nonce, fill.at, hit, order])

  return (
    <div ref={ref} className={cn('rounded-sm', className)}>
      {children}
    </div>
  )
}
